import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { format } from "date-fns";
import DatePicker from "react-datepicker";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST, FORMAT_ADD_DATE, CURRENCY, ROLES } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import FileUploader from "@/components/Fields/FileUploader";
import Loader from "@/components/Layouts/Loader";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { getInvoiceInspectorById, updatePaymentStatusInspector } from "@/services/invoicesInspectorService";
import { getInvoicesShipOwnerById, updatePaymentStatusShipOwner } from "@/services/invoicesShipOwnerService";
import { invoicePaymentSchema } from "@/schemas/invoicePaymentSchema";
import { formatCurrency, getCurrencySymbol } from "@/utils/currency";
import { getExchangeRateDateRange } from '@/services/exchangeRateService';
import CurrencyInput from "../../../components/Fields/CurrencyInput";


const PaymentConfirmation = ({
    hideAmountIDR = true,
}: {
    hideAmountIDR?: boolean;
}) => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id, type } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataDetail, setDataDetail] = useState(null) as any;
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentFile, setPaymentFile] = useState(null) as any;
    const [title, setTitle] = useState("");
    const [dataEdit, setDataEdit] = useState(null) as any;
    const [amountIDR] = useState(0);

    const [latestExchangeRate, setLatestExchangeRate] = useState<any>(null);
    const [exchangeError, setExchangeError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const [loadingExchangeRate, setLoadingExchangeRate] = useState(false);
    const [paymentDate, setPaymentDate] = useState<Date | null>(null);

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Confirm Payment?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = (data: any) => {
        setTitle("Confirm Payment?");
        setConfirmationTest("Are you sure you want to confirm?");
        setConfirmationTitleButton("Confirm Payment");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataPayment = () => {
        const data = new FormData();
        data.append('invoiceId', id);
        data.append('paymentStatus', '1');
        data.append('paymentDate', dataEdit ? format(dataEdit.paymentDate, FORMAT_ADD_DATE) : '');
        data.append('totalAmountIDR', String(amountIDR));
        data.append('modifiedBy', name || 'System');
        data.append('file', paymentFile);
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Confirm Payment?") {
            if (role === 'super admin') {
                navigate(type === 'ship operator' ? '/manage-invoices/ship-operator' : '/manage-invoices/inspecting-company');
            } else {
                if (type === 'inspector') {
                    navigate('/manage-invoices/inspecting-company');
                    return;
                }

                navigate('/manage-invoices-ship-operator');
            }

            return;
        }

        const obj = generateDataPayment();
        let response = {} as any;
        if (type === 'ship owner') {
            response = await updatePaymentStatusShipOwner(obj) as any;
        } else if (type === 'inspector') {
            response = await updatePaymentStatusInspector(obj) as any;
        }

        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: 'Payment confirmed.',
                description: "We've confirmed your payment.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                if (role === 'super admin') {
                    navigate(type === 'ship owner' ? '/manage-invoices/ship-operator' : '/manage-invoices/inspecting-company')
                } else {
                    if (type === 'inspector') {
                        navigate('/manage-invoices/inspecting-company');
                        return;
                    }

                    navigate('/manage-invoices-ship-operator')
                }
            }, 1000)
        } else {
            toast({
                title: 'Payment failed to confirmed.',
                description: "We unabled to confirm your payment.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDrop = (acceptedFiles: File[], setFieldValue: any) => {
        setFieldValue('paymentFile', acceptedFiles[0]);
        setPaymentFile(acceptedFiles[0]);
    };

    const getDataDetailShipOwner = async () => {
        const response = await getInvoicesShipOwnerById(id) as any;
        if (response) {
            setDataDetail(response.data);
        }
        setIsLoading(false);
    }

    const getDataDetailInspector = async () => {
        const response = await getInvoiceInspectorById(id) as any;
        if (response) {
            setDataDetail(response.data);
        }
        setIsLoading(false);
    }

    const getLatestExchangeRate = async (customDate?: string) => {
            setLoadingExchangeRate(true);
            setExchangeError(null);
            try {
                const endDate = customDate
                    ? new Date(customDate)
                    : dataDetail?.paymentDate
                        ? new Date(dataDetail.paymentDate)
                        : new Date(new Date().setDate(new Date().getDate() - 1));
                const end = format(endDate, 'yyyy-MM-dd');
                const startDate = new Date(endDate);
                startDate.setDate(endDate.getDate() - 15);
                const start = format(startDate, 'yyyy-MM-dd');
                const response = await getExchangeRateDateRange(start, end, 1, 10);
                if ((response.status === 200 || response.status === 'OK') && response.data) {
                    setLatestExchangeRate(response.data[0].kurs);
                } else {
                    setExchangeError('Failed to load exchange rate.');
                }
            } catch {
                setExchangeError('Error loading exchange rate.');
            } finally {
                setLoadingExchangeRate(false);
            }
        }

    useEffect(() => {
        if (id && type === 'ship owner') {
            getDataDetailShipOwner();
        } 
        if (id && type === 'inspector') {
            getDataDetailInspector();
        }
    }, [id])

    useEffect(() => {
        if (dataDetail?.currency === CURRENCY.USD.code) {
            getLatestExchangeRate(dataDetail?.paymentDate ? format(new Date(dataDetail.paymentDate), 'yyyy-MM-dd') : undefined);
        }
    }, [dataDetail])

    useEffect(() => {
         getLatestExchangeRate();
    },[retryCount])

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
                    <div className="form-container">
                        <Formik
                            key={dataDetail ? dataDetail.invoiceID || dataDetail.invoiceNumber : 'formik-invoice-payment'}
                            initialValues={{
                                invoiceID: dataDetail?.invoiceID ?? dataDetail?.invoiceNumber,
                                invoiceDate: dataDetail ? format(dataDetail?.invoiceDate, 'dd-MM-yyyy') : '',
                                paymentDate: paymentDate
                                    ? format(paymentDate, FORMAT_ADD_DATE).toString()
                                    : dataDetail?.paymentDate
                                        ? format(new Date(dataDetail.paymentDate), FORMAT_ADD_DATE).toString()
                                        : '',
                                shipName: dataDetail?.shipName,
                                amount: type === 'ship owner' ? (dataDetail?.isKursConversion && dataDetail?.mataUang ? getCurrencySymbol(dataDetail?.mataUang, dataDetail?.conversionAmount) : getCurrencySymbol(dataDetail?.isIndonesia ? 'IDR' : 'USD', dataDetail?.amount)) : formatCurrency(dataDetail?.totalAmount, dataDetail?.currency, dataDetail?.currency === 'IDR' ? 'id-ID' : 'en-US'),
                                amountIDR: dataDetail?.currency && dataDetail?.currency.toLowerCase() !== 'idr'
                                    ? (dataDetail?.totalAmount && latestExchangeRate
                                        ? dataDetail.totalAmount * latestExchangeRate
                                        : '')
                                    : (dataDetail?.totalAmount ?? ''),
                                paymentFile: null
                            }}
                            validationSchema={() => invoicePaymentSchema(role)}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>
                                    <Field name='invoiceID'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.invoiceID && form.touched.invoiceID} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Invoice ID<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Invoice ID' className="form-input" />
                                                        <FormErrorMessage>{form.errors.invoiceID}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='invoiceDate'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.invoiceDate && form.touched.invoiceDate} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Invoice Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Invoice Date' className="form-input" />
                                                        <FormErrorMessage>{form.errors.invoiceDate}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='shipName'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.shipName && form.touched.shipName} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Vessel Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Vessel Name' className="form-input" />
                                                        <FormErrorMessage>{form.errors.shipName}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='amount'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.amount && form.touched.amount} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Amount<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} disabled placeholder='Amount' className="form-input" />
                                                        <FormErrorMessage>{form.errors.amount}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    {
                                        (ROLES.FINANCE.includes(role) && !hideAmountIDR) && (
                                            <div>
                                                <Field name='latestExchangeRate'>
                                                    {({ field }: any) => (
                                                        <FormControl isInvalid={!!exchangeError} style={{ marginBottom: 15 }}>
                                                            <div className="form-field-container">
                                                                <FormLabel className="form-label">Latest Exchange Rate</FormLabel>
                                                                <div className="form-input-container">
                                                                    <Input
                                                                        {...field}
                                                                        value={latestExchangeRate ?? ''}
                                                                        placeholder={loadingExchangeRate ? 'Loading...' : 'Latest exchange rate'}
                                                                        className="form-input"
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            const value = e.target.value;
                                                                            setLatestExchangeRate(value);
                                                                        }}
                                                                        disabled={loadingExchangeRate}
                                                                    />
                                                                    {exchangeError && (
                                                                        <FormErrorMessage>{exchangeError}</FormErrorMessage>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </FormControl>
                                                    )}
                                                </Field>
                                                <Field name='amountIDR'>
                                                {({ field, form }: any) => (
                                                    <FormControl isInvalid={form.errors.amountIDR && form.touched.amountIDR} style={{ marginBottom: 15 }}>
                                                        <div className="form-field-container">
                                                            <FormLabel className="form-label">Amount in IDR</FormLabel>
                                                            <div className="form-input-container">
                                                                <CurrencyInput
                                                                    {...field}
                                                                    placeholder={loadingExchangeRate ? 'Loading...' : 'Latest exchange rate'}
                                                                    prefix={CURRENCY.IDR.code}
                                                                    value={
                                                                        paymentDate && latestExchangeRate
                                                                            ? (dataDetail?.totalAmount * latestExchangeRate || '1')
                                                                            : (dataDetail?.totalAmountIDR ? dataDetail?.totalAmountIDR : dataDetail?.totalAmount * latestExchangeRate || '2')
                                                                    }
                                                                    disabled={true}
                                                                    width="100%"
                                                                />
                                                                <FormErrorMessage>{form.errors.amountIDR}</FormErrorMessage>
                                                                {exchangeError && (
                                                                    <Button size="sm" mt={2} onClick={() => setRetryCount(retryCount + 1)}>
                                                                        Retry
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </FormControl>
                                                )}
                                            </Field>
                                            </div>
                                            
                                        )
                                    }
                                       <Field name='paymentDate'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.paymentDate && form.touched.paymentDate} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Payment Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div style={{
                                                        width: '100%',
                                                        height: '2.5rem',
                                                        fontSize: '1rem',
                                                        paddingInlineStart: '1rem',
                                                        paddingInlineEnd: '1rem',
                                                        borderRadius: '0.375rem',
                                                        minWidth: '0px',
                                                        outline: '2px solid transparent',
                                                        outlineOffset: '2px',
                                                        position: 'relative',
                                                        appearance: 'none',
                                                        transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                                                        paddingTop: '0.35rem',
                                                        marginBottom: (form.errors.paymentDate && form.touched.paymentDate) ? '1.25rem' : 0,
                                                        border: form.errors.paymentDate && form.touched.paymentDate ? '2px solid red' : '1px solid #e5e7e9'
                                                    }}>
                                                        <DatePicker
                                                            id="paymentDate"
                                                            selected={field.value as Date | null}
                                                            onChange={(date: Date | null) => {
                                                                props.setFieldValue('paymentDate', date)
                                                                setPaymentDate(date);
                                                                getLatestExchangeRate(date ? format(date, 'yyyy-MM-dd') : undefined);
                                                            }}
                                                            dateFormat="dd-MM-yyyy"
                                                            placeholderText="Payment Date"
                                                            readOnly={isSubmitting}
                                                            maxDate={new Date()}
                                                            popperPlacement="bottom"
                                                        />
                                                        <FormErrorMessage>{form.errors.paymentDate}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='paymentFile'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.paymentFile && form.submitCount} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Payment File<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <FileUploader onDrop={(e) => handleDrop(e, props.setFieldValue)} isInvalid={form.errors.paymentFile && form.submitCount} isDisabled={props.isSubmitting} type='supporting-files' />
                                                        <FormErrorMessage>{form.errors.paymentFile}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor='white'
                                            borderColor='#D0D5DD'
                                            isLoading={isSubmitting}
                                            marginRight={5}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                        >
                                            Confirm Payment
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default PaymentConfirmation;
